import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API, useLanguage, useTheme } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import AISalesAssistant from "../components/AISalesAssistant";
import {
  ArrowRight, ArrowLeft, MessageSquare, Calendar, Users,
  TrendingUp, RefreshCw, CheckCircle2, Star, Zap, Phone,
  Clock, Bell, Play, ChevronRight, Sun, Moon, Globe, X,
  CalendarCheck, Shield, Scissors, Heart, Sparkles, Activity,
  Stethoscope, Dumbbell, Brain, Briefcase, Building2,
  GraduationCap, PawPrint, Hand
} from "lucide-react";
import axios from "axios";

// Sectors: those with dedicatedDemo route to their tailored demo;
// the rest fall through to the generic /api/demo/start walkthrough.
const SECTORS = [
  { key: "dentist",          it: "Dentista",                en: "Dentist",            icon: Heart,         dedicatedDemo: "/demo-odonto" },
  { key: "fisio",            it: "Fisio · Osteo",           en: "Physio · Osteo",     icon: Activity,      dedicatedDemo: "/demo-benessere-posturale" },
  { key: "beauty",           it: "Centro Estetico",         en: "Beauty Center",      icon: Sparkles,      dedicatedDemo: "/demo-estetica" },
  { key: "barber",           it: "Barbiere / Parrucchiere", en: "Barber / Hair",      icon: Scissors,      dedicatedDemo: "/demo-hair" },
  { key: "doctor",           it: "Medico",                  en: "Doctor",             icon: Stethoscope },
  { key: "psychologist",     it: "Psicologo",               en: "Psychologist",       icon: Brain },
  { key: "personal_trainer", it: "Personal Trainer",        en: "Personal Trainer",   icon: Dumbbell },
  { key: "consultant",       it: "Consulente",              en: "Consultant",         icon: Briefcase },
  { key: "trainer",          it: "Formatore / Coach",       en: "Trainer / Coach",    icon: GraduationCap },
  { key: "vet",              it: "Veterinario",             en: "Veterinarian",       icon: PawPrint },
  { key: "wellness",         it: "Massaggi · Spa",          en: "Massage · Spa",      icon: Hand },
  { key: "other",             it: "Studio Professionale",   en: "Professional Office", icon: Building2 },
];

const STEPS = [
  { key: "intro",    titleIt: "Il tuo cliente scrive su WhatsApp", titleEn: "Your client messages on WhatsApp" },
  { key: "booking",  titleIt: "Prenotazione automatica in tempo reale", titleEn: "Real-time automatic booking" },
  { key: "calendar", titleIt: "L'agenda si riempie da sola", titleEn: "Your calendar fills up automatically" },
  { key: "recovery", titleIt: "I clienti persi tornano automaticamente", titleEn: "Lost clients come back automatically" },
  { key: "results",  titleIt: "I tuoi risultati con SmartBooker", titleEn: "Your results with SmartBooker" },
];

const InteractiveDemoPage = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [phase, setPhase] = useState("form"); // form | walkthrough | cta
  const [step, setStep] = useState(0);
  const [sector, setSector] = useState(searchParams.get("sector") || "doctor");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [demoData, setDemoData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatIdx, setChatIdx] = useState(0);
  const [calVisible, setCalVisible] = useState(0);
  const chatTimer = useRef(null);

  const t = (it, en) => language === "it" ? it : en;

  // Click handler for sector chips: if the sector has a dedicated demo, jump there directly.
  const pickSector = (s) => {
    if (s.dedicatedDemo) {
      // Track jump (fire-and-forget)
      axios.post(`${API}/analytics/track`, {
        event: "demo_sector_redirect", data: { sector: s.key, target: s.dedicatedDemo }
      }).catch(() => {});
      navigate(s.dedicatedDemo);
      return;
    }
    setSector(s.key);
  };

  // Track demo page visit
  useEffect(() => {
    axios.post(`${API}/analytics/track`, { event: "demo_view", referrer: document.referrer || "" }).catch(() => {});
  }, []);

  const startDemo = async () => {
    setLoading(true);
    try {
      const sectorObj = SECTORS.find(x => x.key === sector);
      const res = await axios.post(`${API}/demo/start`, {
        ...form,
        sector,
        sector_label: sectorObj ? (language === "it" ? sectorObj.it : sectorObj.en) : "",
      });
      setDemoData(res.data);
      setSessionId(res.data.session_id);
      setPhase("walkthrough");
      setStep(0);
      trackEvent("demo_started", 0);
      // Also track in site analytics
      axios.post(`${API}/analytics/track`, { event: "demo_started" }).catch(() => {});
    } catch { }
    finally { setLoading(false); }
  };

  const trackEvent = async (event, stepNum) => {
    if (!sessionId && !demoData?.session_id) return;
    try {
      await axios.post(`${API}/demo/track`, {
        session_id: sessionId || demoData?.session_id,
        event, step: stepNum,
      });
    } catch { }
  };

  const nextStep = () => {
    if (step < STEPS.length - 1) {
      const next = step + 1;
      setStep(next);
      trackEvent(`step_${STEPS[next].key}`, next);
    } else {
      setPhase("cta");
      trackEvent("demo_completed", STEPS.length);
    }
  };

  const prevStep = () => { if (step > 0) setStep(step - 1); };

  // Animate WhatsApp chat messages
  useEffect(() => {
    if (phase !== "walkthrough" || step !== 0 || !demoData) return;
    setChatIdx(0);
    const msgs = demoData.wa_chat;
    let i = 0;
    chatTimer.current = setInterval(() => {
      i++;
      if (i >= msgs.length) { clearInterval(chatTimer.current); return; }
      setChatIdx(i);
    }, 1200);
    return () => clearInterval(chatTimer.current);
  }, [phase, step, demoData]);

  // Animate calendar fill
  useEffect(() => {
    if (phase !== "walkthrough" || step !== 2 || !demoData) return;
    setCalVisible(0);
    let count = 0;
    const max = Math.min(demoData.appointments.length, 18);
    const timer = setInterval(() => {
      count++;
      setCalVisible(count);
      if (count >= max) clearInterval(timer);
    }, 120);
    return () => clearInterval(timer);
  }, [phase, step, demoData]);

  const fmt = (v) => `€${(v || 0).toLocaleString("it-IT")}`;

  // ==================== PHASE: FORM ====================
  if (phase === "form") {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex flex-col">
        {/* Nav */}
        <nav className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <img src="/smartbooker-icon-48.png" alt="" className="h-8 w-8" />
            <span className="font-semibold text-lg text-slate-900 dark:text-white">SmartBooker</span>
          </Link>
          <div className="flex items-center gap-1">
            <button onClick={() => changeLanguage(language === "it" ? "en" : "it")} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"><Globe className="w-4 h-4 text-slate-400" /></button>
            <button onClick={toggleTheme} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800">{theme === "light" ? <Moon className="w-4 h-4 text-slate-400" /> : <Sun className="w-4 h-4 text-slate-400" />}</button>
          </div>
        </nav>

        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="max-w-xl w-full space-y-8" data-testid="demo-form">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 mb-6">
                <Play className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">{t("Demo interattiva gratuita", "Free interactive demo")}</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t("Prova SmartBooker in azione", "See SmartBooker in action")}</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-3">{t("Scopri come il sistema automatizza prenotazioni e clienti nel tuo settore", "Discover how the system automates bookings and clients in your sector")}</p>
            </div>

            {/* Sector selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t("Il tuo settore", "Your sector")}</label>
              <p className="text-[11px] text-slate-400 dark:text-slate-500 -mt-0.5">
                {t("I settori con la spunta hanno una demo dedicata che parte all'istante.", "Checked sectors have a dedicated demo that starts instantly.")}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {SECTORS.map(s => {
                  const Icon = s.icon;
                  const isSelected = sector === s.key;
                  const hasDedicated = !!s.dedicatedDemo;
                  return (
                    <button
                      key={s.key}
                      onClick={() => pickSector(s)}
                      className={`relative p-3 rounded-xl border text-left transition-all hover:-translate-y-0.5 ${isSelected ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 shadow-sm" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-slate-300"}`}
                      data-testid={`demo-sector-${s.key}`}
                    >
                      <div className="flex items-start gap-2">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSelected ? "text-emerald-500" : "text-slate-400"}`} />
                        <span className="text-xs font-medium leading-tight">{t(s.it, s.en)}</span>
                      </div>
                      {hasDedicated && (
                        <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-cyan-500 flex items-center justify-center" title={t("Demo dedicata disponibile", "Dedicated demo available")}>
                          <Play className="w-2 h-2 text-white" fill="white" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Form fields */}
            <div className="space-y-3">
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t("Il tuo nome", "Your name")} className="h-12 rounded-xl" data-testid="demo-name" />
              <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@esempio.com" type="email" className="h-12 rounded-xl" data-testid="demo-email" />
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder={t("Telefono (opzionale)", "Phone (optional)")} className="h-12 rounded-xl" data-testid="demo-phone" />
            </div>

            <button onClick={startDemo} disabled={loading}
              className="w-full h-12 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50"
              data-testid="demo-start-btn"
            >
              {loading ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />{t("Caricamento...", "Loading...")}</span>
                : <span className="flex items-center justify-center gap-2">{t("Inizia la Demo", "Start Demo")}<ArrowRight className="w-4 h-4" /></span>}
            </button>

            <p className="text-center text-xs text-slate-400">{t("Nessuna carta richiesta. 2 minuti.", "No card required. 2 minutes.")}</p>
          </div>
        </div>
      </div>
    );
  }

  // ==================== PHASE: CTA ====================
  if (phase === "cta") {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-lg text-center space-y-8" data-testid="demo-cta">
          <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t("Hai visto come funziona!", "You've seen how it works!")}</h1>
          <p className="text-lg text-slate-500 dark:text-slate-400">{t("Vuoi attivarlo per la tua attività? Ti configuriamo tutto in meno di 24h.", "Want to activate it for your business? We set everything up in less than 24h.")}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register">
              <Button size="lg" className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-8 py-6 text-lg shadow-xl shadow-emerald-500/20" data-testid="demo-cta-activate">
                {t("Attiva SmartBooker", "Activate SmartBooker")} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="mailto:info@smartbooker.it">
              <Button size="lg" variant="outline" className="rounded-full px-8 py-6 text-lg" data-testid="demo-cta-contact">
                {t("Richiedi una demo guidata", "Request a guided demo")}
              </Button>
            </a>
          </div>
          <div className="flex items-center justify-center gap-1.5 text-slate-400">
            <Shield className="w-3.5 h-3.5" />
            <span className="text-xs">{t("Utilizzato da professionisti in tutta Italia", "Used by professionals across Italy")}</span>
          </div>
        </div>
        <AISalesAssistant sessionId={sessionId} currentStep={5} sector={sector} language={language} />
      </div>
    );
  }

  // ==================== PHASE: WALKTHROUGH ====================
  if (!demoData) return null;
  const currentStep = STEPS[step];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col" data-testid="demo-walkthrough">
      {/* Top bar */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-3">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/smartbooker-icon-48.png" alt="" className="h-7 w-7" />
            <span className="font-semibold text-sm text-slate-900 dark:text-white">{demoData.salon_name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">DEMO</span>
          </div>
          {/* Step indicator */}
          <div className="hidden sm:flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i <= step ? "bg-emerald-500 w-8" : "bg-slate-200 dark:bg-slate-700 w-4"}`} />
            ))}
          </div>
          <Link to="/" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"><X className="w-5 h-5" /></Link>
        </div>
      </div>

      {/* Step title banner */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-500 py-4 px-4 text-center">
        <p className="text-sm text-white/70">{t("Step", "Step")} {step + 1}/{STEPS.length}</p>
        <h2 className="text-lg sm:text-xl font-bold text-white" data-testid="demo-step-title">{t(currentStep.titleIt, currentStep.titleEn)}</h2>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-8 max-w-5xl mx-auto w-full">
        {/* STEP 0: WhatsApp Chat */}
        {step === 0 && (
          <div className="max-w-md mx-auto">
            <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-xl">
              {/* WA header */}
              <div className="bg-emerald-600 px-4 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-white" /></div>
                <div><p className="font-semibold text-white text-sm">{demoData.salon_name}</p><p className="text-[10px] text-emerald-100">SmartBooker Bot</p></div>
              </div>
              {/* Messages */}
              <div className="bg-[#e5ddd5] dark:bg-slate-800 p-4 space-y-2 min-h-[320px]" data-testid="demo-wa-chat">
                {demoData.wa_chat.slice(0, chatIdx + 1).map((msg, i) => (
                  <div key={i} className={`flex ${msg.from === "client" ? "justify-end" : "justify-start"}`} style={{ animation: "fadeUp 0.3s ease-out" }}>
                    <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm shadow-sm ${msg.from === "client" ? "bg-emerald-100 dark:bg-emerald-800/60 text-slate-900 dark:text-white rounded-tr-sm" : "bg-white dark:bg-slate-700 text-slate-900 dark:text-white rounded-tl-sm"}`}>
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Booking flow */}
        {step === 1 && (
          <div className="max-w-lg mx-auto space-y-4">
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg p-6 space-y-5" data-testid="demo-booking">
              <h3 className="font-semibold text-slate-900 dark:text-white">{t("Servizi disponibili", "Available services")}</h3>
              {demoData.services.map((svc, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${i === 0 ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20" : "border-slate-100 dark:border-slate-700"}`}>
                  <div><p className="font-medium text-sm text-slate-900 dark:text-white">{svc.name}</p><p className="text-xs text-slate-500">{svc.duration}min</p></div>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">€{svc.price}</span>
                </div>
              ))}
              <div className="pt-2 flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="w-5 h-5" />
                <p className="text-sm font-medium">{t("Il cliente sceglie, il bot prenota. Zero attese.", "Client chooses, bot books. Zero wait.")}</p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Calendar */}
        {step === 2 && (
          <div className="max-w-2xl mx-auto" data-testid="demo-calendar">
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4"><Calendar className="w-5 h-5 text-emerald-500" /><h3 className="font-semibold text-slate-900 dark:text-white">{t("Agenda della settimana", "Week schedule")}</h3></div>
              <div className="grid grid-cols-5 gap-2">
                {["Lun", "Mar", "Mer", "Gio", "Ven"].map((day, di) => (
                  <div key={di}>
                    <p className="text-xs font-medium text-slate-500 text-center mb-2">{day}</p>
                    <div className="space-y-1">
                      {demoData.appointments.filter((_, ai) => ai % 5 === di).slice(0, 5).map((appt, ai) => {
                        const globalIdx = di * 5 + ai;
                        const visible = globalIdx < calVisible;
                        return (
                          <div key={ai} className={`text-[10px] rounded-lg px-2 py-1.5 transition-all duration-300 ${visible ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 opacity-100 scale-100" : "bg-slate-50 dark:bg-slate-800 text-transparent opacity-0 scale-95"}`}>
                            {visible ? <><span className="font-bold">{appt.time}</span> {appt.client.split(" ")[0]}</> : "—"}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center gap-2">
                <CalendarCheck className="w-5 h-5 text-emerald-500" />
                <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">{t(`${Math.min(calVisible, 18)} prenotazioni confermate automaticamente`, `${Math.min(calVisible, 18)} bookings confirmed automatically`)}</p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Recovery */}
        {step === 3 && (
          <div className="max-w-lg mx-auto space-y-4" data-testid="demo-recovery">
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-lg p-6 space-y-4">
              <div className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-amber-500" /><h3 className="font-semibold text-slate-900 dark:text-white">{t("Recupero clienti automatico", "Automatic client recovery")}</h3></div>
              {demoData.recovery.map((r, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100 dark:border-slate-700">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${r.status === "recovered" ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-amber-100 dark:bg-amber-900/30"}`}>
                    {r.status === "recovered" ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Clock className="w-5 h-5 text-amber-500" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-900 dark:text-white">{r.client}</p>
                    <p className="text-xs text-slate-500">{t(`Inattivo da ${r.days_inactive} giorni`, `Inactive for ${r.days_inactive} days`)}</p>
                  </div>
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${r.status === "recovered" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"}`}>
                    {r.status === "recovered" ? t("Recuperato", "Recovered") : t("Follow-up inviato", "Follow-up sent")}
                  </span>
                </div>
              ))}
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 text-sm text-amber-700 dark:text-amber-300">
                {t("SmartBooker identifica i clienti inattivi e li riporta automaticamente.", "SmartBooker identifies inactive clients and brings them back automatically.")}
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Results Dashboard */}
        {step === 4 && (
          <div className="max-w-2xl mx-auto space-y-4" data-testid="demo-results">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: t("Fatturato mese", "Monthly revenue"), value: fmt(demoData.stats.revenue_month), icon: TrendingUp, color: "from-emerald-500 to-teal-600" },
                { label: t("Clienti attivi", "Active clients"), value: demoData.stats.clients_active, icon: Users, color: "from-blue-500 to-indigo-600" },
                { label: t("Prenotazioni", "Bookings"), value: demoData.stats.bookings_month, icon: CalendarCheck, color: "from-violet-500 to-purple-600" },
                { label: t("Via WhatsApp", "Via WhatsApp"), value: demoData.stats.wa_bookings, icon: MessageSquare, color: "from-green-500 to-emerald-600" },
                { label: "No-show", value: `${demoData.stats.no_show_rate}%`, icon: Bell, color: "from-amber-500 to-orange-600" },
                { label: t("Valutazione", "Rating"), value: "4.9", icon: Star, color: "from-rose-500 to-pink-600" },
              ].map((s, i) => (
                <div key={i} className={`bg-gradient-to-br ${s.color} rounded-2xl p-4 text-white`} style={{ animation: `fadeUp 0.4s ease-out ${i * 100}ms both` }}>
                  <s.icon className="w-6 h-6 opacity-80 mb-1" />
                  <p className="text-2xl font-bold">{s.value}</p>
                  <p className="text-xs text-white/70">{s.label}</p>
                </div>
              ))}
            </div>
            <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-5 text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400">{t("Questi sono risultati reali di attività che usano SmartBooker. Il tuo business potrebbe ottenere gli stessi.", "These are real results from businesses using SmartBooker. Your business could achieve the same.")}</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom navigation */}
      <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Button variant="ghost" onClick={prevStep} disabled={step === 0} className="rounded-full" data-testid="demo-prev"><ArrowLeft className="w-4 h-4 mr-1" />{t("Indietro", "Back")}</Button>
          <span className="text-xs text-slate-400">{step + 1} / {STEPS.length}</span>
          <Button onClick={nextStep} className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6" data-testid="demo-next">
            {step === STEPS.length - 1 ? t("Completa", "Complete") : t("Avanti", "Next")} <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* AI Sales Assistant */}
      <AISalesAssistant sessionId={sessionId} currentStep={step} sector={sector} language={language} />
    </div>
  );
};

export default InteractiveDemoPage;
